import type {
  ChapterPlan,
  ContinuityObjectState,
  InspectorAuthoritativeBeatId,
  InspectorBeatRepairRequest,
  Ledger,
  PhysicalContinuityObligation,
  PhysicalContinuityObligationType,
  PhysicalObjectMobilityStatus,
  PhysicalObjectOperationalStatus,
  PhysicalObjectType,
} from '../types';
import { FOUR_BEAT_SEPARATOR } from './fourBeatChapterAssembly.service';
import {
  buildStableObjectId,
  resolveContinuityObjectIdentity,
} from './physicalObjectIdentity.service';

const OBJECT_TYPES = new Set<PhysicalObjectType>([
  'VEHICLE', 'WEAPON', 'TOOL', 'ARTIFACT', 'MACHINE', 'ANIMAL_EQUIPMENT', 'CONTAINER', 'OTHER',
]);
const OPERATIONAL_STATUSES = new Set<PhysicalObjectOperationalStatus>([
  'OPERATIONAL', 'DAMAGED', 'DISABLED', 'DESTROYED', 'ABANDONED', 'MISSING', 'UNKNOWN',
]);
const MOBILITY_STATUSES = new Set<PhysicalObjectMobilityStatus>([
  'MOBILE', 'IMMOBILE', 'TOWED', 'CARRIED', 'LEFT_BEHIND', 'UNKNOWN',
]);
const OBLIGATION_TYPES = new Set<PhysicalContinuityObligationType>([
  'EXPLAIN_LOCATION', 'EXPLAIN_ABANDONMENT', 'EXPLAIN_REPAIR', 'EXPLAIN_TRANSFER',
  'EXPLAIN_DESTRUCTION', 'EXPLAIN_RECOVERY',
]);

const DEPARTURE_PATTERN =
  /\b(?:walk(?:ed|ing|s)?\s+toward|head(?:ed|ing|s)?\s+toward|start(?:ed|ing|s)?\s+toward|set\s+out\s+toward|depart(?:ed|ing|s)?|leav(?:e|es|ing)\s+the\s+(?:area|region|perimeter)|on\s+foot(?:\s+toward)?|made\s+for\s+the|drove\s+toward|drive\s+toward)\b/i;

const RESOLUTION_PATTERN =
  /\b(?:left\s+behind|leave\s+behind|remained\s+behind|stayed\s+behind|where\s+it\s+had\s+stalled|still\s+(?:outside|there)|abandoned|seized|towed|repaired|fixed\s+the|dead\s+flatbed|disabled\s+flatbed|remains\s+outside|remained\s+outside|without\s+the\s+(?:vehicle|flatbed|truck))\b/i;

const VEHICLE_USE_PATTERN =
  /\b(?:drove|drive|driving|climbed\s+into|behind\s+the\s+wheel|started\s+the\s+engine|pulled\s+away|rolled\s+out)\b/i;

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 48);
}

export { buildStableObjectId } from './physicalObjectIdentity.service';

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => readString(entry)).filter((entry): entry is string => Boolean(entry));
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function normalizeContinuityObjectState(
  raw: unknown,
  chapterNumber: number,
  index: number,
  context?: {
    projectId?: string;
    existingObjects?: ContinuityObjectState[];
  },
): ContinuityObjectState | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const canonicalName =
    readString(record.canonicalName) ||
    readString(record.name) ||
    readString(record.objectName);
  if (!canonicalName) return null;

  const objectTypeRaw = readString(record.objectType)?.toUpperCase() as PhysicalObjectType | undefined;
  const objectType = objectTypeRaw && OBJECT_TYPES.has(objectTypeRaw) ? objectTypeRaw : 'OTHER';

  const operationalRaw = readString(record.operationalStatus)?.toUpperCase() as PhysicalObjectOperationalStatus | undefined;
  const operationalStatus =
    operationalRaw && OPERATIONAL_STATUSES.has(operationalRaw) ? operationalRaw : 'UNKNOWN';

  const mobilityRaw = readString(record.mobilityStatus)?.toUpperCase() as PhysicalObjectMobilityStatus | undefined;
  const mobilityStatus =
    mobilityRaw && MOBILITY_STATUSES.has(mobilityRaw) ? mobilityRaw : undefined;

  const importanceRaw = readString(record.continuityImportance)?.toUpperCase();
  const continuityImportance =
    importanceRaw === 'BACKGROUND' || importanceRaw === 'RELEVANT' || importanceRaw === 'REQUIRED_CARRYOVER'
      ? importanceRaw
      : operationalStatus === 'DISABLED' || operationalStatus === 'DAMAGED'
        ? 'REQUIRED_CARRYOVER'
        : 'RELEVANT';

  const ownerRaw = record.ownerCharacterIds ?? record.owners ?? record.owner;
  const ownerCharacterIds = Array.isArray(ownerRaw)
    ? readStringArray(ownerRaw)
    : readString(ownerRaw)
      ? [readString(ownerRaw)!]
      : [];

  const locationHint =
    readString(record.currentLocationDescription) || readString(record.location) || readString(record.currentLocation);

  const suppliedObjectId = readString(record.objectId) || readString(record.id) || undefined;
  const resolution = resolveContinuityObjectIdentity({
    projectId: context?.projectId,
    chapterNumber,
    index,
    canonicalName,
    objectType,
    aliases: readStringArray(record.aliases),
    ownerCharacterIds,
    suppliedObjectId,
    existingObjects: context?.existingObjects,
  });

  const objectId = resolution.objectId;

  return {
    objectId,
    canonicalName,
    aliases: readStringArray(record.aliases),
    objectType,
    ownerCharacterIds,
    currentHolderCharacterId: readString(record.currentHolderCharacterId ?? record.holder),
    currentLocationId: readString(record.currentLocationId),
    currentLocationDescription:
      readString(record.currentLocationDescription) || readString(record.location) || readString(record.currentLocation),
    operationalStatus,
    mobilityStatus,
    lastSeenChapter: readNumber(record.lastSeenChapter) ?? chapterNumber,
    lastChangedChapter: readNumber(record.lastChangedChapter) ?? chapterNumber,
    sourceChapterId: readString(record.sourceChapterId) || String(resolution.identity.firstSeenChapter),
    sourceRevisionId: readString(record.sourceRevisionId),
    continuityImportance,
    unresolvedObligation: readString(record.unresolvedObligation) || undefined,
  };
}

export function normalizePhysicalContinuityObligation(
  raw: unknown,
  chapterNumber: number,
  index: number,
): PhysicalContinuityObligation | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const description = readString(record.description);
  const objectId = readString(record.objectId);
  if (!description || !objectId) return null;

  const obligationRaw = readString(record.obligation)?.toUpperCase() as PhysicalContinuityObligationType | undefined;
  const obligation =
    obligationRaw && OBLIGATION_TYPES.has(obligationRaw) ? obligationRaw : 'EXPLAIN_LOCATION';

  const statusRaw = readString(record.status)?.toUpperCase();
  const status =
    statusRaw === 'OPEN' || statusRaw === 'CARRIED_FORWARD' || statusRaw === 'RESOLVED' ? statusRaw : 'OPEN';

  return {
    obligationId:
      readString(record.obligationId) ||
      readString(record.id) ||
      `obligation_${chapterNumber}_${index + 1}_${slugify(objectId)}`,
    objectId,
    originatingChapter: readNumber(record.originatingChapter) ?? chapterNumber,
    status,
    obligation,
    description,
    requiredByChapter: readNumber(record.requiredByChapter),
  };
}

export function deriveObligationsFromObjects(
  objects: ContinuityObjectState[],
  chapterNumber: number,
): PhysicalContinuityObligation[] {
  const obligations: PhysicalContinuityObligation[] = [];

  for (const object of objects) {
    if (object.continuityImportance === 'BACKGROUND') continue;
    if (object.operationalStatus !== 'DISABLED' && object.operationalStatus !== 'DAMAGED') continue;
    if (object.mobilityStatus === 'LEFT_BEHIND' || object.operationalStatus === 'ABANDONED') continue;

    const owner = object.ownerCharacterIds[0] || object.currentHolderCharacterId || 'the owner';
    const location = object.currentLocationDescription || object.currentLocationId || 'the last known location';
    obligations.push({
      obligationId: `obligation_${chapterNumber}_${slugify(object.objectId)}`,
      objectId: object.objectId,
      originatingChapter: object.lastChangedChapter,
      status: 'OPEN',
      obligation:
        object.operationalStatus === 'DISABLED' && object.objectType === 'VEHICLE'
          ? 'EXPLAIN_ABANDONMENT'
          : 'EXPLAIN_LOCATION',
      description:
        object.unresolvedObligation ||
        `${owner}'s ${object.canonicalName} remains ${object.operationalStatus.toLowerCase()} at ${location} and must be repaired, moved, abandoned, seized, or explicitly left behind before ${owner} departs.`,
      requiredByChapter: chapterNumber + 1,
    });
  }

  return obligations;
}

export function mergePhysicalContinuityLedger(
  existing: Ledger,
  rawData: Record<string, unknown>,
  chapterNumber: number,
  context?: { projectId?: string },
): Pick<Ledger, 'continuity_objects' | 'physical_continuity_obligations'> {
  const incomingObjects = Array.isArray(rawData.continuity_objects) ? rawData.continuity_objects : [];
  const normalizedIncoming = incomingObjects
    .map((entry, index) =>
      normalizeContinuityObjectState(entry, chapterNumber, index, {
        projectId: context?.projectId,
        existingObjects: existing.continuity_objects,
      }),
    )
    .filter((entry): entry is ContinuityObjectState => Boolean(entry));

  const objectMap = new Map<string, ContinuityObjectState>();
  for (const object of existing.continuity_objects || []) {
    objectMap.set(object.objectId, object);
  }
  for (const object of normalizedIncoming) {
    const prior = objectMap.get(object.objectId);
    objectMap.set(object.objectId, prior ? { ...prior, ...object, lastChangedChapter: chapterNumber } : object);
  }

  const continuity_objects = [...objectMap.values()];
  const derived = deriveObligationsFromObjects(normalizedIncoming, chapterNumber);

  const obligationMap = new Map<string, PhysicalContinuityObligation>();
  for (const obligation of existing.physical_continuity_obligations || []) {
    if (obligation.status !== 'RESOLVED') {
      obligationMap.set(obligation.obligationId, obligation);
    }
  }

  const incomingObligations = Array.isArray(rawData.physical_continuity_obligations)
    ? rawData.physical_continuity_obligations
    : [];
  for (const [index, entry] of incomingObligations.entries()) {
    const normalized = normalizePhysicalContinuityObligation(entry, chapterNumber, index);
    if (normalized) obligationMap.set(normalized.obligationId, normalized);
  }
  for (const obligation of derived) {
    if (![...obligationMap.values()].some((entry) => entry.objectId === obligation.objectId && entry.status === 'OPEN')) {
      obligationMap.set(obligation.obligationId, obligation);
    }
  }

  return {
    continuity_objects,
    physical_continuity_obligations: [...obligationMap.values()],
  };
}

export function getOpenPhysicalObligations(
  ledger: Ledger,
  chapterNumber: number,
): PhysicalContinuityObligation[] {
  return (ledger.physical_continuity_obligations || []).filter(
    (obligation) => obligation.status === 'OPEN' && obligation.originatingChapter < chapterNumber,
  );
}

export function getRelevantContinuityObjects(
  ledger: Ledger,
  plan: ChapterPlan | undefined,
  chapterNumber: number,
): ContinuityObjectState[] {
  const focus = new Set((plan?.characterFocus || []).map((name) => name.toLowerCase()));
  const locationFocus = new Set((plan?.locationFocus || []).map((name) => name.toLowerCase()));

  return (ledger.continuity_objects || []).filter((object) => {
    if (object.continuityImportance === 'REQUIRED_CARRYOVER') return true;
    if (object.lastSeenChapter >= chapterNumber - 1 && object.continuityImportance !== 'BACKGROUND') return true;
    if (object.ownerCharacterIds.some((owner) => focus.has(owner.toLowerCase()))) return true;
    if (
      object.currentLocationDescription &&
      locationFocus.has(object.currentLocationDescription.toLowerCase())
    ) {
      return true;
    }
    return false;
  });
}

function describeCurrentPhysicalState(object: ContinuityObjectState): string {
  const owner = object.currentHolderCharacterId || object.ownerCharacterIds[0] || 'Someone';
  const location = object.currentLocationDescription || object.currentLocationId || 'an unknown location';
  const status = object.operationalStatus.toLowerCase();
  const mobility = object.mobilityStatus ? `, ${object.mobilityStatus.toLowerCase()}` : '';
  if (object.objectType === 'VEHICLE' && object.operationalStatus === 'DISABLED') {
    return `${owner}'s ${object.canonicalName} is disabled at ${location}.`;
  }
  if (object.mobilityStatus === 'CARRIED') {
    return `${owner} currently carries ${object.canonicalName}.`;
  }
  return `${object.canonicalName} is ${status}${mobility} at ${location}.`;
}

function describeRecentPhysicalConsequence(
  object: ContinuityObjectState,
  chapterNumber: number,
): string | null {
  if (object.lastChangedChapter !== chapterNumber - 1) return null;
  if (object.operationalStatus === 'DISABLED' && object.objectType === 'VEHICLE') {
    return `Previous chapter: ${object.canonicalName} failed and became immobile.`;
  }
  if (object.operationalStatus === 'DESTROYED') {
    return `Previous chapter: ${object.canonicalName} was destroyed.`;
  }
  if (object.mobilityStatus === 'CARRIED' && object.continuityImportance === 'REQUIRED_CARRYOVER') {
    return `Previous chapter: ${object.canonicalName} changed possession or location.`;
  }
  return null;
}

export function buildPhysicalContinuityContextSections(
  ledger: Ledger,
  plan: ChapterPlan | undefined,
  chapterNumber: number,
): {
  currentState: string;
  openObligations: string;
  recentConsequences: string;
  combined: string;
} {
  const obligations = getOpenPhysicalObligations(ledger, chapterNumber);
  const objects = getRelevantContinuityObjects(ledger, plan, chapterNumber);
  const objectById = new Map(objects.map((object) => [object.objectId, object]));

  const currentStateLines = objects
    .filter((object) => object.continuityImportance !== 'BACKGROUND')
    .map((object) => `- ${describeCurrentPhysicalState(object)}`);

  const obligationLines = obligations.map((obligation) => {
    const object = objectById.get(obligation.objectId);
    const suffix = object?.operationalStatus === 'DISABLED' && object.objectType === 'VEHICLE'
      ? ' It must remain behind, be repaired, moved, transferred, destroyed, or explicitly abandoned before it can disappear from the story.'
      : '';
    return `- ${obligation.description}${suffix}`;
  });

  const consequenceLines = objects
    .map((object) => describeRecentPhysicalConsequence(object, chapterNumber))
    .filter((line): line is string => Boolean(line))
    .map((line) => `- ${line}`);

  const currentState = currentStateLines.length
    ? `CURRENT RELEVANT PHYSICAL STATE\n${currentStateLines.join('\n')}`
    : '';
  const openObligations = obligationLines.length
    ? `OPEN PHYSICAL CONTINUITY OBLIGATIONS\n${obligationLines.join('\n')}`
    : '';
  const recentConsequences = consequenceLines.length
    ? `RECENT PHYSICAL CONSEQUENCES\n${consequenceLines.join('\n')}`
    : '';

  const combined = [currentState, openObligations, recentConsequences].filter(Boolean).join('\n\n');
  return { currentState, openObligations, recentConsequences, combined };
}

export function buildPhysicalContinuityLookbackSection(
  ledger: Ledger,
  plan: ChapterPlan | undefined,
  chapterNumber: number,
): string {
  return buildPhysicalContinuityContextSections(ledger, plan, chapterNumber).combined;
}

export function buildPhysicalContinuityScribeRule(): string {
  return `
PHYSICAL OBJECT CONTINUITY RULE:
Do not make a character depart, arrive, travel, fight, trade, or retrieve equipment in a way that ignores an unresolved physical-object state.
Every REQUIRED_CARRYOVER object must be visibly retained in the scene, moved, repaired, transferred, abandoned, destroyed, or explicitly carried forward.
One clear sentence may satisfy an open obligation; do not force lengthy exposition.
`.trim();
}

function objectAliases(object: ContinuityObjectState | undefined, obligation: PhysicalContinuityObligation): string[] {
  const aliases = new Set<string>();
  if (object?.canonicalName) aliases.add(object.canonicalName.toLowerCase());
  for (const alias of object?.aliases || []) aliases.add(alias.toLowerCase());
  const words = obligation.description.toLowerCase().split(/\s+/);
  for (const token of words) {
    if (token.length >= 5) aliases.add(token.replace(/[^a-z0-9]/g, ''));
  }
  return [...aliases].filter(Boolean);
}

function sentenceReferencesObject(sentence: string, aliases: string[]): boolean {
  const lower = sentence.toLowerCase();
  return aliases.some((alias) => alias.length >= 4 && lower.includes(alias));
}

function obligationResolvedInText(text: string, aliases: string[]): boolean {
  if (RESOLUTION_PATTERN.test(text)) {
    return aliases.length === 0 || sentenceReferencesObject(text, aliases);
  }
  const repairPattern = /\b(?:patch(?:ed|ing)?|repair(?:ed|ing)?|refill(?:ed|ing)?|fixed|engine held|steadied|coolant)\b/i;
  if (repairPattern.test(text) && (aliases.length === 0 || sentenceReferencesObject(text, aliases) || /flatbed|vehicle|truck|transport/i.test(text))) {
    return true;
  }
  return false;
}

function obligationResolvedBeforeSentence(content: string, sentence: string, aliases: string[]): boolean {
  if (obligationResolvedInText(content, aliases)) return true;
  const offset = content.indexOf(sentence);
  if (offset <= 0) return false;
  return obligationResolvedInText(content.slice(0, offset), aliases);
}

export interface SuspectedPhysicalContinuityFinding {
  assessment: 'SUSPECTED';
  code: 'SUSPECTED_PHYSICAL_CONTINUITY' | 'MISSING_OBJECT_CARRYOVER';
  obligationId: string;
  objectId: string;
  suspectedBeatId?: InspectorAuthoritativeBeatId;
  sentence: string;
  reason: string;
  previousState: string;
  minimalCorrection: string;
  highConfidence: boolean;
}

export function assessSuspectedPhysicalContinuity(
  content: string,
  ledger: Ledger,
  chapterNumber: number,
): SuspectedPhysicalContinuityFinding[] {
  const obligations = getOpenPhysicalObligations(ledger, chapterNumber);
  if (!obligations.length) return [];

  const objects = ledger.continuity_objects || [];
  const findings: SuspectedPhysicalContinuityFinding[] = [];
  const beats = (content || '').split(FOUR_BEAT_SEPARATOR);

  for (let beatIndex = 0; beatIndex < beats.length; beatIndex += 1) {
    const beatText = beats[beatIndex] || '';
    const sentences = beatText.split(/(?<=[.!?])\s+/).map((sentence) => sentence.trim()).filter(Boolean);

    for (const sentence of sentences) {
      if (!DEPARTURE_PATTERN.test(sentence)) continue;

      for (const obligation of obligations) {
        const object = objects.find((entry) => entry.objectId === obligation.objectId);
        const aliases = objectAliases(object, obligation);
        const ownerInObligation = object?.ownerCharacterIds.some((owner) =>
          obligation.description.toLowerCase().includes(owner.toLowerCase()),
        );
        const ownerMentioned =
          !object?.ownerCharacterIds.length ||
          object.ownerCharacterIds.some((owner) => new RegExp(`\\b${owner}\\b`, 'i').test(sentence)) ||
          new RegExp(`\\b${object?.currentHolderCharacterId || ''}\\b`, 'i').test(sentence) ||
          (ownerInObligation && DEPARTURE_PATTERN.test(sentence));

        if (!ownerMentioned && !sentenceReferencesObject(sentence, aliases)) continue;
        if (obligationResolvedBeforeSentence(content, sentence, aliases)) continue;

        const usesDestroyedObject =
          object?.operationalStatus === 'DESTROYED' &&
          (VEHICLE_USE_PATTERN.test(sentence) || DEPARTURE_PATTERN.test(sentence));

        const usesVehicleWithoutRepair =
          object?.objectType === 'VEHICLE' &&
          object.operationalStatus === 'DISABLED' &&
          VEHICLE_USE_PATTERN.test(sentence);

        if (object?.operationalStatus === 'DESTROYED' && !usesDestroyedObject) continue;

        findings.push({
          assessment: 'SUSPECTED',
          code: usesDestroyedObject || usesVehicleWithoutRepair ? 'MISSING_OBJECT_CARRYOVER' : 'SUSPECTED_PHYSICAL_CONTINUITY',
          obligationId: obligation.obligationId,
          objectId: obligation.objectId,
          suspectedBeatId: `beat-${Math.min(4, beatIndex + 1)}` as InspectorAuthoritativeBeatId,
          sentence,
          reason: obligation.description,
          previousState: object
            ? `${object.canonicalName} (${object.operationalStatus}${object.mobilityStatus ? `, ${object.mobilityStatus}` : ''}) at ${object.currentLocationDescription || 'unknown location'}`
            : obligation.description,
          minimalCorrection:
            object?.objectType === 'VEHICLE'
              ? `Establish that ${object.canonicalName} remains behind, is repaired, moved, seized, or abandoned before the owner departs.`
              : `Resolve the open physical obligation for ${object?.canonicalName || 'the object'} before incompatible travel or use.`,
          highConfidence: Boolean(object && (object.continuityImportance === 'REQUIRED_CARRYOVER' || object.objectType === 'VEHICLE')),
        });
      }
    }
  }

  return findings;
}

export function buildPhysicalContinuityInspectorContext(
  findings: SuspectedPhysicalContinuityFinding[],
): string {
  if (!findings.length) return '';

  const lines = findings.map((finding) => {
    const beat = finding.suspectedBeatId ? ` suspectedBeatId=${finding.suspectedBeatId}` : '';
    const confidence = finding.highConfidence ? ' confidence=high' : ' confidence=low';
    return `- [${finding.code}] obligationId=${finding.obligationId} objectId=${finding.objectId}${beat}${confidence} previousState="${finding.previousState}" sentence="${finding.sentence}" minimalCorrection="${finding.minimalCorrection}"`;
  });

  return `
DETERMINISTIC SUSPECTED PHYSICAL CONTINUITY PRECHECK (ADVISORY — NOT A TERMINAL BLOCK):
${lines.join('\n')}
Inspector must confirm or reject each suspicion. Return REPAIR_REQUIRED with reasonCodes ["UNRESOLVED_PHYSICAL_CONTINUITY"] or ["MISSING_OBJECT_CARRYOVER"] and the authoritative beatId only on confirmation.
`.trim();
}

export function inspectorConfirmsPhysicalContinuity(params: {
  reason?: string;
  correction_order?: string;
  beatRepairRequests?: InspectorBeatRepairRequest[];
}): boolean {
  if (
    params.beatRepairRequests?.some((request) =>
      request.reasonCodes.some((code) =>
        code === 'UNRESOLVED_PHYSICAL_CONTINUITY' || code === 'MISSING_OBJECT_CARRYOVER',
      ),
    )
  ) {
    return true;
  }
  const combined = `${params.reason || ''} ${params.correction_order || ''}`.toUpperCase();
  return combined.includes('UNRESOLVED_PHYSICAL_CONTINUITY') || combined.includes('MISSING_OBJECT_CARRYOVER');
}

export function resolveConfirmedPhysicalContinuityRepair(params: {
  suspectedFindings: SuspectedPhysicalContinuityFinding[];
  beatRepairRequests: InspectorBeatRepairRequest[];
  reason?: string;
  correction_order?: string;
}): InspectorBeatRepairRequest | undefined {
  const confirmed = inspectorConfirmsPhysicalContinuity({
    reason: params.reason,
    correction_order: params.correction_order,
    beatRepairRequests: params.beatRepairRequests,
  });
  if (!confirmed) return undefined;

  const fromInspector = params.beatRepairRequests.find((request) =>
    request.reasonCodes.some(
      (code) => code === 'UNRESOLVED_PHYSICAL_CONTINUITY' || code === 'MISSING_OBJECT_CARRYOVER',
    ),
  );
  if (fromInspector?.beatId) return fromInspector;

  const highConfidence = params.suspectedFindings.find((finding) => finding.highConfidence && finding.suspectedBeatId);
  if (!highConfidence?.suspectedBeatId) return fromInspector;

  return {
    beatId: highConfidence.suspectedBeatId,
    reasonCodes: [highConfidence.code === 'MISSING_OBJECT_CARRYOVER' ? 'MISSING_OBJECT_CARRYOVER' : 'UNRESOLVED_PHYSICAL_CONTINUITY'],
    explanation:
      params.correction_order ||
      `Rewrite ${highConfidence.suspectedBeatId}: ${highConfidence.minimalCorrection}`,
  };
}

export interface PhysicalContinuityAcceptanceResult {
  isBlocked: boolean;
  warnings: string[];
  unresolvedObligationIds: string[];
}

export function evaluatePhysicalContinuityAcceptance(
  prose: string,
  ledger: Ledger,
  chapterNumber: number,
): PhysicalContinuityAcceptanceResult {
  const obligations = getOpenPhysicalObligations(ledger, chapterNumber);
  const warnings: string[] = [];
  const unresolvedObligationIds: string[] = [];
  let isBlocked = false;

  if (!obligations.length || !prose.trim()) {
    return { isBlocked, warnings, unresolvedObligationIds };
  }

  const findings = assessSuspectedPhysicalContinuity(prose, ledger, chapterNumber);
  for (const finding of findings) {
    if (!finding.highConfidence) {
      warnings.push(`Physical continuity warning: ${finding.reason}`);
      continue;
    }
    isBlocked = true;
    unresolvedObligationIds.push(finding.obligationId);
    warnings.push(
      `BLOCKING PHYSICAL CONTINUITY: ${finding.reason} (${finding.minimalCorrection})`,
    );
  }

  return { isBlocked, warnings, unresolvedObligationIds };
}

export function markObligationsResolvedByChapter(
  ledger: Ledger,
  prose: string,
  chapterNumber: number,
): PhysicalContinuityObligation[] {
  const obligations = ledger.physical_continuity_obligations || [];
  if (!obligations.length || !prose.trim()) return obligations;

  return obligations.map((obligation) => {
    if (obligation.status === 'RESOLVED' || obligation.originatingChapter >= chapterNumber) {
      return obligation;
    }
    const object = (ledger.continuity_objects || []).find((entry) => entry.objectId === obligation.objectId);
    const aliases = objectAliases(object, obligation);
    if (!obligationResolvedInText(prose, aliases)) return obligation;
    return { ...obligation, status: 'RESOLVED' as const };
  });
}

/** Test fixture: Chapter 2 Mara flatbed state */
export function buildMaraFlatbedChapter2Fixture(chapterNumber = 2): {
  continuity_objects: ContinuityObjectState[];
  physical_continuity_obligations: PhysicalContinuityObligation[];
} {
  const flatbed: ContinuityObjectState = {
    objectId: 'vehicle_mara_flatbed_ch2',
    canonicalName: 'stripped flatbed transport',
    aliases: ['flatbed', 'flatbed transport', 'Mara flatbed'],
    objectType: 'VEHICLE',
    ownerCharacterIds: ['Mara'],
    currentHolderCharacterId: 'Mara',
    currentLocationDescription: "outside Steve's perimeter",
    operationalStatus: 'DISABLED',
    mobilityStatus: 'IMMOBILE',
    lastSeenChapter: chapterNumber,
    lastChangedChapter: chapterNumber,
    sourceChapterId: String(chapterNumber),
    continuityImportance: 'REQUIRED_CARRYOVER',
    unresolvedObligation:
      "Mara's disabled flatbed remains outside Steve's perimeter and must be repaired, moved, abandoned, seized, or explicitly left behind before Mara departs.",
  };

  return {
    continuity_objects: [flatbed],
    physical_continuity_obligations: [
      {
        obligationId: 'obligation_mara_flatbed_ch2',
        objectId: flatbed.objectId,
        originatingChapter: chapterNumber,
        status: 'OPEN',
        obligation: 'EXPLAIN_ABANDONMENT',
        description: flatbed.unresolvedObligation!,
        requiredByChapter: chapterNumber + 1,
      },
    ],
  };
}

export const CHAPTER_3_MINIMAL_FLATBED_CORRECTION =
  "Mara adjusted the strap of her diagnostic kit and started toward the northern pass, leaving the dead flatbed where it had stalled outside Steve's perimeter.";
